/**
 * Regroupement des camions proches, sans dépendance.
 *
 * Les positions sont projetées en pixels « monde » au zoom courant (Mercator,
 * comme la carte) puis rangées dans une grille de `cellPx` pixels. Une case qui
 * contient plusieurs camions devient un groupe ; une case seule reste un camion.
 * La projection ne dépend ni du cadrage ni de la taille de la fenêtre : un
 * panoramique ne change donc jamais la composition des groupes, seul un
 * changement de zoom le fait.
 *
 * `react-leaflet-cluster` n'est pas dans le projet et `react-leaflet` 5 /
 * React 19 imposent de vérifier chaque version ; une grille de pixels est
 * courte, testable et suffit à une flotte de quelques centaines de camions.
 */
import L from 'leaflet';

export const CLUSTER_CELL_PX = 56;
/** Au-delà de ce zoom, chaque camion est dessiné seul (rue par rue). */
export const CLUSTER_DISABLE_AT_ZOOM = 15;

/**
 * @param {Array<{key: string, latitude: number, longitude: number}>} items
 * @param {{zoom: number, cellPx?: number, disableAtZoom?: number, soloKeys?: Set<string>}} options
 *   `soloKeys` : camions jamais regroupés (le camion sélectionné).
 * @returns {{singles: Array, clusters: Array<{key: string, items: Array, latitude: number, longitude: number}>}}
 */
export function grouperParGrille(items, { zoom, cellPx = CLUSTER_CELL_PX, disableAtZoom = CLUSTER_DISABLE_AT_ZOOM, soloKeys } = {}) {
  const liste = Array.isArray(items) ? items : [];
  if (!Number.isFinite(zoom) || zoom >= disableAtZoom) return { singles: liste, clusters: [] };

  const cases = new Map();
  const singles = [];
  liste.forEach((item) => {
    if (soloKeys && soloKeys.has(item.key)) {
      singles.push(item);
      return;
    }
    const point = L.CRS.EPSG3857.latLngToPoint(L.latLng(item.latitude, item.longitude), zoom);
    const id = `${Math.floor(point.x / cellPx)}:${Math.floor(point.y / cellPx)}`;
    const groupe = cases.get(id);
    if (groupe) groupe.push(item);
    else cases.set(id, [item]);
  });

  const clusters = [];
  cases.forEach((groupe) => {
    if (groupe.length === 1) {
      singles.push(groupe[0]);
      return;
    }
    const latitude = groupe.reduce((somme, item) => somme + item.latitude, 0) / groupe.length;
    const longitude = groupe.reduce((somme, item) => somme + item.longitude, 0) / groupe.length;
    clusters.push({
      key: `groupe:${groupe.map((item) => item.key).sort().join('|')}`,
      items: groupe,
      latitude,
      longitude,
    });
  });

  return { singles, clusters };
}
